import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import {
  Loader2, Upload, CheckCircle2, ArrowLeft, ExternalLink,
  Briefcase, Sparkles, AlertTriangle, TrendingUp, FileText,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", color: "#0A66C2" },
  { key: "naukri", label: "Naukri", color: "#4A96D2" },
  { key: "indeed", label: "Indeed", color: "#003A9B" },
  { key: "shine", label: "Shine", color: "#6C3EB6" },
];

export default function JobMatch() {
  const { orderId } = useParams();
  const [phase, setPhase] = useState("polling"); // polling | ready | uploading | report | failed
  const [attempts, setAttempts] = useState(0);
  const [report, setReport] = useState(null);
  const [file, setFile] = useState(null);
  const [location, setLocation] = useState("India");
  const fileRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!orderId) return setPhase("failed");
    // Try to load existing report first (idempotent re-visit)
    (async () => {
      try {
        const r = await api.get(`/jobs/report/${orderId}`);
        setReport(r.data);
        setPhase("report");
        return;
      } catch {}
      // Poll payment status
      const MAX = 15;
      const poll = async (n) => {
        try {
          const s = await api.get(`/payments/status/${orderId}`);
          setAttempts(n + 1);
          if (s.data.payment_status === "paid") { setPhase("ready"); return; }
          if (s.data.payment_status === "failed" || s.data.status === "expired") { setPhase("failed"); return; }
        } catch {}
        if (n >= MAX) { setPhase("failed"); return; }
        timerRef.current = setTimeout(() => poll(n + 1), 1500);
      };
      poll(0);
    })();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [orderId]);

  const onFilePick = (f) => {
    if (!f) return;
    const okTypes = /\.(pdf|docx|txt)$/i;
    if (!okTypes.test(f.name)) return toast.error("Please upload a PDF, DOCX, or TXT file.");
    if (f.size > 3_000_000) return toast.error("File too large (max 3 MB).");
    setFile(f);
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a resume file first.");
    setPhase("uploading");
    const fd = new FormData();
    fd.append("resume", file);
    fd.append("location", location || "India");
    try {
      const res = await fetch(`${API}/jobs/analyze/${orderId}`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Analysis failed");
      }
      const data = await res.json();
      setReport(data);
      setPhase("report");
    } catch (e) {
      toast.error(e.message);
      setPhase("ready");
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="jobs-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C5C55] hover:text-[#002FA7] mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        {phase === "polling" && (
          <div className="text-center py-24" data-testid="jobs-polling">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7] mx-auto mb-4" />
            <p className="overline">Confirming payment ({attempts}/15)</p>
          </div>
        )}

        {phase === "failed" && (
          <div className="text-center py-24" data-testid="jobs-failed">
            <AlertTriangle className="w-8 h-8 text-[#FF2A00] mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-black tracking-tight">Something went wrong</h1>
            <p className="mt-3 text-[#5C5C55]">Payment could not be confirmed. Please head back and try again.</p>
            <Link to="/dashboard" className="btn-primary mt-6 inline-flex">Back to dashboard</Link>
          </div>
        )}

        {(phase === "ready" || phase === "uploading") && (
          <div data-testid="jobs-upload">
            <div className="overline mb-3 flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" /> Job Match Report</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">Upload your resume.</h1>
            <p className="mt-3 text-[#5C5C55] max-w-2xl">
              We&apos;ll analyze it against your chosen career, generate personalized search links across
              LinkedIn, Naukri, Indeed and Shine, and give you specific edits to make your resume land.
            </p>

            <div className="mt-8 border-2 border-dashed border-[#E5E5E0] bg-white rounded-sm p-8 text-center">
              <input
                ref={fileRef} type="file" accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => onFilePick(e.target.files?.[0])}
                data-testid="resume-file-input"
              />
              {file ? (
                <div className="space-y-3" data-testid="resume-selected">
                  <FileText className="w-8 h-8 text-[#002FA7] mx-auto" />
                  <div className="font-semibold">{file.name}</div>
                  <div className="text-xs text-[#5C5C55]">{(file.size / 1024).toFixed(0)} KB</div>
                  <button onClick={() => setFile(null)} className="text-xs text-[#5C5C55] underline">Change file</button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-8 h-8 text-[#5C5C55] mx-auto" />
                  <div className="font-semibold">Drop your resume here or click to browse</div>
                  <div className="text-xs text-[#5C5C55]">PDF, DOCX or TXT · max 3 MB</div>
                  <button onClick={() => fileRef.current?.click()} data-testid="pick-file-btn" className="btn-secondary mt-2">Choose file</button>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="overline mb-2 block">Location for job search</label>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Bengaluru, Mumbai, Remote, India"
                  data-testid="location-input"
                  className="w-full bg-white border border-[#E5E5E0] rounded-sm p-3 text-base focus:border-[#002FA7] outline-none"
                />
              </div>
              <button
                onClick={upload}
                disabled={!file || phase === "uploading"}
                data-testid="analyze-resume-btn"
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {phase === "uploading" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing your resume…</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Analyze &amp; find jobs</>
                )}
              </button>
            </div>
          </div>
        )}

        {phase === "report" && report && (
          <div data-testid="jobs-report" className="space-y-10">
            <div>
              <div className="overline mb-2">Report ready · {new Date(report.generated_at).toLocaleString()}</div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
                Jobs for <span className="text-[#002FA7]">{report.career_title}</span>
              </h1>
              <p className="mt-3 text-[#5C5C55]">
                Curated across four platforms · location: <b>{report.location}</b>
              </p>
            </div>

            {/* Platform search buttons */}
            <section>
              <h2 className="font-heading text-2xl font-bold mb-4">Search openings now</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {PLATFORMS.map((p) => (
                  <a
                    key={p.key}
                    href={report.platform_urls?.[p.key]}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`platform-${p.key}`}
                    className="border border-[#E5E5E0] bg-white p-5 rounded-sm hover:border-[#002FA7]/50 transition-colors group"
                  >
                    <div className="font-heading font-bold text-lg" style={{ color: p.color }}>{p.label}</div>
                    <div className="text-xs text-[#5C5C55] mt-1 truncate font-mono">{report.search_keywords}</div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#0A0A0A] font-semibold">
                      Open in {p.label} <ExternalLink className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </a>
                ))}
              </div>
              {report.title_urls?.length > 0 && (
                <div className="mt-5 text-sm">
                  <div className="overline mb-2">Or try these variants</div>
                  <div className="flex flex-wrap gap-2">
                    {report.title_urls.map((t, i) => (
                      <a key={i} href={t.urls.linkedin} target="_blank" rel="noopener noreferrer"
                         className="text-xs font-mono px-3 py-1.5 border border-[#E5E5E0] bg-white rounded-sm hover:border-[#002FA7]"
                         data-testid={`variant-${i}`}>
                        {t.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Resume vs Career report */}
            <section className="grid md:grid-cols-2 gap-5">
              <div className="border border-[#E5E5E0] bg-white rounded-sm p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-[#166534]" />
                  <h3 className="font-heading text-lg font-bold">Your strengths</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  {report.resume_strengths?.map((s, i) => (
                    <li key={i} data-testid={`strength-${i}`} className="flex gap-2"><span className="text-[#166534] mt-0.5">▸</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#E5E5E0] bg-white rounded-sm p-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-[#FF2A00]" />
                  <h3 className="font-heading text-lg font-bold">Skills to add</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  {report.skill_gaps?.map((s, i) => (
                    <li key={i} data-testid={`gap-${i}`} className="flex gap-2"><span className="text-[#FF2A00] mt-0.5">▸</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#E5E5E0] bg-white rounded-sm p-6 md:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-[#002FA7]" />
                  <h3 className="font-heading text-lg font-bold">Edit your resume today</h3>
                </div>
                <ol className="space-y-2 text-sm list-decimal ml-5">
                  {report.resume_edits?.map((s, i) => (
                    <li key={i} data-testid={`edit-${i}`}>{s}</li>
                  ))}
                </ol>
              </div>
              {report.cover_letter_tip && (
                <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-white rounded-sm p-6 md:col-span-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">Cold outreach lead</div>
                  <div className="font-heading text-lg font-bold">{report.cover_letter_tip}</div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
